import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from "react";
import PropTypes from "prop-types";
import { jwtDecode } from "jwt-decode";
import { queryClient } from "src/tanstack/QueryClient";
import { AuthContext } from "./auth-context";
import { axiosInstance } from "src/config/axiosInstance";
import { endpoints } from "src/utils/axios";
import { storageKeys } from "src/utils";
import { useLocales } from "src/locales";
import { initialState } from "./auth-reducer-state";
import { paths } from "src/routes/paths";
import { useGlobalDialogContext } from "src/components/global-dialog";
import { usePathname, useRouter } from "src/routes/hooks";
import { PATH_AFTER_LOGIN } from "src/config-global";
import { useSnackbar } from "src/components/snackbar";

const ACTION_TYPES = {
    INITIAL: "INITIAL",
    LOGIN: "LOGIN",
    REGISTER: "REGISTER",
    LOGOUT: "LOGOUT",
    NO_SESSION: "NO-SESSION",
    USER_PARTIAL: "USER-PARTIAL",
    COMPANY_PARTIAL: "COMPANY-PARTIAL",
};

const SESSION_RENEW_TIMEOUT_MS = 9_000;
const SESSION_RENEW_TIMEOUT_RETRIES = 2;
const SESSION_RENEW_RETRY_DELAY_MS = 1_000;
const SESSION_RENEW_BEFORE_EXPIRATION_MS = 120_000;

const normalizeExpToMs = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value > 1_000_000_000_000 ? value : value * 1000;
    }

    if (typeof value === "string" && value.trim().length > 0) {
        const numericValue = Number(value);

        if (Number.isFinite(numericValue)) {
            return numericValue > 1_000_000_000_000
                ? numericValue
                : numericValue * 1000;
        }

        const parsedDate = Date.parse(value);
        if (!Number.isNaN(parsedDate)) {
            return parsedDate;
        }
    }

    return null;
};

const reducer = (state, action) => {
    switch (action.type) {
        case ACTION_TYPES.INITIAL:
            return {
                loading: false,
                user: action.payload.user,
                /*  role: action.payload.role, */ // Set the user's role during initialization
                paymentMethods: action.payload.paymentMethods,
                isValidSubscription: action.payload.isValidSubscription,
            };
        case ACTION_TYPES.LOGIN:
            return {
                ...state,
                user: action.payload.user,
                paymentMethods: action.payload.paymentMethods,
                isValidSubscription: action.payload.isValidSubscription,
                loading: false,
            };
        case ACTION_TYPES.REGISTER:
            return {
                ...state,
                user: action.payload.user,
            };
        case ACTION_TYPES.LOGOUT:
        case ACTION_TYPES.NO_SESSION:
            return {
                ...state,
                loading: false,
                user: null,
                paymentMethods: [],
                isValidSubscription: false,
            };
        case ACTION_TYPES.USER_PARTIAL:
            return {
                ...state,
                user: { ...state.user, ...action.payload.user },
            };
        case ACTION_TYPES.COMPANY_PARTIAL:
            return {
                ...state,
                user: {
                    ...state.user,
                    company: {
                        ...state.user.company,
                        ...action.payload.company,
                    },
                },
            };
        default:
            return state;
    }
};

export function AuthProvider({ children }) {
    const { t, setClientLang } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const pathname = usePathname();
    const router = useRouter();
    const isFetchingSession = useRef(false);
    const { triggerDialog } = useGlobalDialogContext();

    const [state, dispatch] = useReducer(reducer, initialState);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [userToken, setUserToken] = useState(null);
    const [expTimeSession, setExpTimeSession] = useState(null);
    const isRenewingTokenRef = useRef(false);

    const setSessionOnLogin = useCallback(
        ({ userSession, setLang = true }) => {
            const { company } = userSession || {};
            // Save to sessionStorage
            sessionStorage.setItem(
                storageKeys.USER,
                JSON.stringify({
                    ...userSession,
                    token: null,
                    superAdminToken: null,
                }),
            );

            sessionStorage.setItem(
                storageKeys.USER_NAME,
                userSession?.userName,
            );
            sessionStorage.setItem(storageKeys.TOKEN, userSession.token);

            if (userSession.superAdminToken) {
                sessionStorage.setItem(
                    storageKeys.SUPERADMIN_TOKEN,
                    userSession.superAdminToken,
                );
            }

            if (userSession.country) {
                sessionStorage.setItem(
                    storageKeys.USER_COUNTRY,
                    userSession.country,
                );
            }
            sessionStorage.setItem(storageKeys.USER_NAME, userSession?.name);
            sessionStorage.setItem(storageKeys.USER_ID, userSession?.userId);
            sessionStorage.setItem(
                storageKeys.USER_CITY,
                userSession?.userCity,
            );
            sessionStorage.setItem(storageKeys.USER_LAT, userSession?.userLat);
            sessionStorage.setItem(
                storageKeys.USER_LONG,
                userSession?.userLong,
            );
            sessionStorage.setItem(
                storageKeys.USER_ROLE,
                userSession?.userRole,
            );
            sessionStorage.setItem(
                storageKeys.NOTIFICATION_ALERT,
                JSON.stringify(userSession.notificationAlert),
            );
            sessionStorage.setItem(storageKeys.COMPANY_ID, company?.companyId);
            sessionStorage.setItem(storageKeys.CURRENCY, userSession?.currency);
            sessionStorage.setItem(storageKeys.LOGO, company?.avatarUrl);
            sessionStorage.setItem(
                storageKeys.COMPANY_NAME,
                company?.companyName,
            );
            sessionStorage.setItem(storageKeys.COMPANY_EMAIL, company?.email);
            sessionStorage.setItem(storageKeys.COMPANY_VAT, company?.vat || "");
            sessionStorage.setItem(storageKeys.BANK_NAME, company?.bankName);
            sessionStorage.setItem(
                storageKeys.BANK_ACCOUNT,
                company?.bankAccount,
            );
            sessionStorage.setItem(storageKeys.SWIFT, company?.swift);
            sessionStorage.setItem(storageKeys.EMAIL, company?.email);

            // Set language if required
            // if (setLang) {
            setClientLang(userSession?.language || "English");
            // }
            sessionStorage.setItem(
                storageKeys.BANK_ACCOUNT_OWNER,
                company?.bankAccountOwner,
            );
            sessionStorage.setItem(
                storageKeys.COMPANY_PARENT_NAME,
                company?.companyParentName,
            );
            sessionStorage.setItem(
                storageKeys.ONBOARDING_STATUS,
                company?.onboardingStatus,
            );
            sessionStorage.setItem(
                storageKeys.APPLY_TAX,
                userSession?.applicableTax,
            );
            sessionStorage.setItem(
                storageKeys.SUBSCRIPTION_PLAN,
                userSession?.subscriptionPlan,
            );
            sessionStorage.setItem(
                storageKeys.PAYMENT_METHODS,
                userSession?.paymentMethods,
            );
            sessionStorage.setItem(
                storageKeys.HOTELS_FEE,
                userSession?.feesHotels,
            );
            sessionStorage.setItem(
                storageKeys.ACTIVITIES_FEE,
                userSession?.fees.activitiesFee,
            );
            sessionStorage.setItem(
                storageKeys.TRANSFERS_FEE,
                userSession?.fees.transfersFee,
            );
            sessionStorage.setItem(storageKeys.CARS_FEE, userSession?.feesCars);
            sessionStorage.setItem(
                storageKeys.FLIGTHS_DOMESTIC_FEE,
                userSession?.fees.flightsDomesticFee,
            );
            sessionStorage.setItem(
                storageKeys.FLIGTHS_INTERNATIONAL_FEE,
                userSession?.fees.flightsInternationalFee,
            );
            sessionStorage.setItem(
                storageKeys.CRUISES_FEE,
                userSession?.fees.CruisesFee,
            );
            sessionStorage.setItem(
                storageKeys.PACKAGE_FEE,
                userSession?.fees.packagesFee,
            );
            setUserToken({
                jwt: userSession.token ?? null,
                superJwt: userSession.superAdminToken ?? null,
            });
        },
        [setClientLang],
    );

    const invalidateQueries = useCallback(() => {
        const queries = queryClient.getQueriesData();
        if (queries.length === 0) return;
        queryClient.removeQueries({
            predicate: () => true,
        });
    }, []);

    const initialize = useCallback(async () => {
        if (isFetchingSession.current) {
            return;
        }
        isFetchingSession.current = true;
        try {
            const response = await axiosInstance.get(endpoints.auth.me);
            if (!response.data?.userSession) {
                throw new Error("Session Expired");
            }
            const { userSession, paymentMethods, isValidSubscription } =
                response.data;
            sessionStorage.setItem(storageKeys.TOKEN, userSession.token);
            setSessionOnLogin({
                userSession,
                setLang: false,
            });
            dispatch({
                type: ACTION_TYPES.INITIAL,
                payload: {
                    user: userSession,
                    paymentMethods,
                    isValidSubscription,
                },
            });
            ///VERY IMPORTANT PLEASE DO NOT DELETE THE user-signup label PORQUE SIRVE PARA TRACKEAR CONVERSIONES
            if (pathname.includes("/travel-agencies/modern/")) {
                router.push(paths.dashboard.home.root + "?user-signup");
            }
            isFetchingSession.current = false;
            return;
        } catch (error) {
            console.error("Token fetch failed", error);
            router.push(paths.dashboard.home.root);
        }
        sessionStorage.clear();
        setUserToken(null);
        setExpTimeSession(null);
        dispatch({
            type: ACTION_TYPES.NO_SESSION,
        });
        isFetchingSession.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invalidateQueries]);

    useEffect(() => {
        initialize();
    }, [initialize]);

    const signIn = useCallback(
        async (responseData, redirect = true) => {
            try {
                const { userSession, isValidSubscription, paymentMethods } =
                    responseData;

                setSessionOnLogin({
                    userSession,
                });

                dispatch({
                    type: ACTION_TYPES.LOGIN,
                    payload: {
                        user: userSession,
                        isValidSubscription,
                        paymentMethods,
                    },
                });

                if (redirect) {
                    router.push(paths.dashboard.travelPreferences);
                }
            } catch (error) {
                console.error("Error en inicio de sesión:", error);
            }
        },
        [router, setSessionOnLogin],
    );
    const fetchLogin = useCallback(
        async (email, password, forceLogin = false, keepSession = false) => {
            try {
                const params = {};
                if (forceLogin) {
                    params.forceLogin = true;
                }
                if (keepSession) {
                    params.keepSession = true;
                }
                const response = await axiosInstance.post(
                    endpoints.auth.login,
                    {
                        email,
                        password,
                    },
                    { params },
                );
                const { userSession } = response.data;
                if (!userSession.isEmailConfigured) {
                    triggerDialog({
                        title: "Your Account is almost ready!",
                        content: {
                            text: "Configure your email server in<br/><strong>Support Center > Onboarding > Step 2: Connect Your Email</strong><br/>to enable this feature",
                            type: "html",
                        },
                        action: {
                            type: "redirect",
                            text: "Configure Email",
                            href: paths.dashboard.user.account,
                        },
                    });
                }

                signIn(response.data, false);

                return { status: "SUCCESS" };
            } catch (error) {
                // if (error?.loginDate) {
                //     return {
                //         status: "ACTIVE_SESSION",
                //         responseData: {
                //             loginDate: error.loginDate,
                //             device: error.device,
                //         },
                //     };
                // }
                if (error === "confirm-email") {
                    return { status: "USER_EMAIL_NOT_CONFIRMED" };
                }
                if (error === "bad_provider") {
                    return { status: "USER_USE_OTHER_PROVIDER" };
                }
                return { status: "ERROR" };
            }
        },
        [signIn],
    );

    const adminLogin = useCallback(
        async (userId) => {
            try {
                const response = await axiosInstance.post(
                    endpoints.users.adminLogin(userId),
                );
                const { userSession, isValidSubscription, paymentMethods } =
                    response.data;
                setSessionOnLogin({ userSession });

                dispatch({
                    type: ACTION_TYPES.LOGIN,
                    payload: {
                        user: userSession,
                        isValidSubscription,
                        paymentMethods,
                    },
                });
                return true;
            } catch (error) {
                console.error("Error en adminLogin:", error);
                return false;
            }
        },
        [setSessionOnLogin],
    );

    const register = useCallback(
        async (userSession, isValidSubscription, paymentMethods) => {
            setSessionOnLogin({ userSession });

            dispatch({
                type: ACTION_TYPES.LOGIN,
                payload: {
                    user: userSession,
                    isValidSubscription,
                    paymentMethods,
                },
            });
            triggerDialog({
                title: "Your Account is almost ready!",
                content: {
                    text: "Configure your email server in<br/><strong>Support Center > Onboarding > Step 2: Connect Your Email</strong><br/>to enable this feature",
                    type: "html",
                },
                action: {
                    type: "redirect",
                    text: "Configure Email",
                    href: paths.dashboard.user.account,
                },
            });
            router.push(PATH_AFTER_LOGIN);
        },
        [router, setSessionOnLogin],
    );

    const logout = useCallback(async () => {
        const keepSessionSelection = localStorage.getItem(
            storageKeys.KEEP_SESSION_ACTIVE,
        );
        try {
            const maxRetries = 3;
            let retries = 0;
            while (retries < maxRetries) {
                try {
                    await axiosInstance.post(endpoints.auth.logout);
                    break;
                } catch (error) {
                    retries++;
                    if (retries === maxRetries) {
                        throw error;
                    }
                    await new Promise((resolve) =>
                        setTimeout(() => resolve(true), 1000),
                    );
                }
            }
            // Limpio local y session storage después del logout para poder cerrar correctamente la sesión
            invalidateQueries();
            sessionStorage.clear();
            localStorage.clear();
            if (keepSessionSelection) {
                localStorage.setItem(storageKeys.KEEP_SESSION_ACTIVE, "true");
            }
            sessionStorage.clear();
            router.replace("/");
            setUserToken(null);
            setExpTimeSession(null);
            dispatch({
                type: ACTION_TYPES.LOGOUT,
            });
            return true;
        } catch (err) {
            console.error("Error en logout:", err);
            enqueueSnackbar(t("Something went wrong"), { variant: "error" });
            return false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invalidateQueries]);

    const forceLogoutToLogin = useCallback(() => {
        const keepSessionSelection = localStorage.getItem(
            storageKeys.KEEP_SESSION_ACTIVE,
        );

        invalidateQueries();
        sessionStorage.clear();
        localStorage.clear();

        if (keepSessionSelection) {
            localStorage.setItem(storageKeys.KEEP_SESSION_ACTIVE, "true");
        }

        setUserToken(null);
        setExpTimeSession(null);
        dispatch({
            type: ACTION_TYPES.LOGOUT,
        });
        router.replace(paths.auth.jwt.login);
    }, [invalidateQueries, router]);

    const updateUserState = useCallback(
        (partialUser) => {
            dispatch({
                type: ACTION_TYPES.USER_PARTIAL,
                payload: {
                    user: partialUser,
                },
            });
            sessionStorage.setItem(
                storageKeys.USER,
                JSON.stringify({
                    ...state.user,
                    ...partialUser,
                }),
            );
        },
        [state],
    );
    const updateUserCompanyState = useCallback((partialCompany) => {
        dispatch({
            type: ACTION_TYPES.COMPANY_PARTIAL,
            payload: {
                company: partialCompany,
            },
        });
    }, []);

    const renewTokenFunction = useCallback(async () => {
        let timeoutRetries = 0;

        while (timeoutRetries < SESSION_RENEW_TIMEOUT_RETRIES) {
            const abortController = new AbortController();
            const timeoutId = setTimeout(
                () => abortController.abort("renew-token-timeout"),
                SESSION_RENEW_TIMEOUT_MS,
            );

            try {
                const renewResponse = await axiosInstance.post(
                    endpoints.auth.renewToken,
                    {},
                    {
                        signal: abortController.signal,
                    },
                );

                return renewResponse;
            } catch (error) {
                const isTimeoutError =
                    error?.name === "CanceledError" ||
                    error?.code === "ERR_CANCELED";

                if (isTimeoutError) {
                    timeoutRetries += 1;

                    if (timeoutRetries >= SESSION_RENEW_TIMEOUT_RETRIES) {
                        throw new Error("RENEW_TIMEOUT_MAX_RETRIES");
                    }

                    await new Promise((resolve) =>
                        setTimeout(resolve, SESSION_RENEW_RETRY_DELAY_MS),
                    );
                    continue;
                }

                if (error === "Unable to refresh token") {
                    throw new Error("UNABLE_TO_REFRESH_TOKEN");
                }

                throw error;
            } finally {
                clearTimeout(timeoutId);
            }
        }

        throw new Error("RENEW_TIMEOUT_MAX_RETRIES");
    }, []);

    // CHECK USER JWT FOR REFRESH TOKEN OR AUTO SIGN OUT
    const renewToken = useCallback(
        async (expDate) => {
            if (isRenewingTokenRef.current) {
                return;
            }

            isRenewingTokenRef.current = true;

            try {
                const res = await renewTokenFunction();
                setUserToken({
                    jwt: res.data.jwt ?? null,
                    superJwt: res.data.superJwt ?? null,
                });
                setExpTimeSession(null);
                return;
            } catch (error) {
                if (error?.message === "UNABLE_TO_REFRESH_TOKEN") {
                    enqueueSnackbar(
                        t("Warning: Your session is about to expire"),
                        {
                            variant: "warning",
                            autoHideDuration: 5000,
                        },
                    );
                    setExpTimeSession(expDate);
                    return;
                }

                if (error?.message === "RENEW_TIMEOUT_MAX_RETRIES") {
                    enqueueSnackbar(t("Session Expired"), {
                        variant: "error",
                    });
                    forceLogoutToLogin();
                    return;
                }

                enqueueSnackbar(t("Invalid Session"), { variant: "error" });
                forceLogoutToLogin();
            } finally {
                isRenewingTokenRef.current = false;
            }
        },
        [enqueueSnackbar, forceLogoutToLogin, renewTokenFunction, t],
    );

    const getSessionExpirationDate = useCallback(() => {
        const tokenToDecode = userToken?.superJwt || userToken?.jwt;

        if (!tokenToDecode) {
            return null;
        }

        try {
            const decoded = jwtDecode(tokenToDecode);
            const accessExpDate = normalizeExpToMs(decoded?.exp);
            const refreshExpCandidates = [
                decoded?.refreshExp,
                decoded?.refreshTokenExp,
                decoded?.refreshTokenExpiresAt,
                decoded?.refresh_token_exp,
            ]
                .map(normalizeExpToMs)
                .filter((value) => Number.isFinite(value));

            const refreshExpDate = refreshExpCandidates.length
                ? Math.min(...refreshExpCandidates)
                : null;

            if (accessExpDate && refreshExpDate) {
                return Math.min(accessExpDate, refreshExpDate);
            }

            return accessExpDate ?? refreshExpDate ?? null;
        } catch (error) {
            return null;
        }
    }, [userToken?.jwt, userToken?.superJwt]);

    useEffect(() => {
        if (!userToken?.jwt && !userToken?.superJwt) return;

        const sessionExpDate = getSessionExpirationDate();

        if (!sessionExpDate) {
            return;
        }

        const renewDate = sessionExpDate - SESSION_RENEW_BEFORE_EXPIRATION_MS;
        const now = Date.now();

        if (now >= renewDate) {
            renewToken(sessionExpDate);
            return;
        }

        const interval = setInterval(() => {
            if (Date.now() >= renewDate) {
                clearInterval(interval);
                renewToken(sessionExpDate);
            }
        }, 30_000);

        return () => {
            clearInterval(interval);
        };
    }, [
        userToken?.jwt,
        userToken?.superJwt,
        renewToken,
        getSessionExpirationDate,
    ]);

    useEffect(() => {
        if (!expTimeSession) return;

        // Validate immediately when the effect is triggered
        const now = Date.now();
        if (now > expTimeSession) {
            enqueueSnackbar(t("Session Expired"), { variant: "error" });
            logout();
            return;
        }
        // Check exp time session for auto logout
        const interval = setInterval(() => {
            const now = Date.now();
            if (now > expTimeSession) {
                clearInterval(interval);
                enqueueSnackbar(t("Session Expired"), {
                    variant: "error",
                });
                logout();
            }
        }, 30_000);
        return () => {
            clearInterval(interval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expTimeSession, logout]);
    // =================================================

    const status = state.loading
        ? "loading"
        : state.user
        ? "authenticated"
        : "unauthenticated";

    const memoizedValue = useMemo(
        () => ({
            user: state.user,
            paymentMethods: state.paymentMethods,
            isValidSubscription: state.isValidSubscription,
            method: "jwt",
            loading: status === "loading",
            authenticated: status === "authenticated",
            unauthenticated: status === "unauthenticated",
            //
            paymentMethod,
            userInfo,
            fetchLogin,
            signIn,
            adminLogin,
            register,
            logout,
            updateUserState,
            updateUserCompanyState,
            setPaymentMethod,
            setUserInfo,
        }),
        [
            state.user,
            state.paymentMethods,
            state.isValidSubscription,
            status,
            paymentMethod,
            userInfo,
            fetchLogin,
            signIn,
            adminLogin,
            logout,
            register,
            updateUserState,
            updateUserCompanyState,
            setPaymentMethod,
            setUserInfo,
        ],
    );

    return (
        <AuthContext.Provider value={memoizedValue}>
            {children}
        </AuthContext.Provider>
    );
}

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
